import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import i18n from '../../i18n';
import { USER_DATA_EXPIRATION_MS } from '../../utils/constants';
import { 
    timeCapsuleApi, 
    VisitorSignupData, 
    VisitorSignupResponse, 
    SelfieUploadResponse,
    ApiError,
    dataURLtoBlob 
} from '../../services/apiService';
import { AgeGroupValue } from '../../utils/constants';

export interface UserState {
    isAuthenticated: boolean;
    visitorId: string; // Added for API integration
    name: string;
    email: string;
    ageGroup: AgeGroupValue | '';
    nationality: string;
    selfieUrl: string;
    language: string;
    hasCompletedRegistration: boolean;
    privacyAccepted: boolean;
    message: string;
    // API loading states
    isLoading: boolean;
    apiError: string | null;
    // Timestamp tracking for automatic expiration
    createdAt: number | null; // When user data was first created
    lastActivityAt: number | null; // Last time user was active
    // Flag to indicate if data was expired and reset
    dataWasExpired: boolean;
}

// Helper function to get initial visitor ID from localStorage
const getInitialVisitorId = (): string => {
    if (typeof window !== 'undefined') {
        return localStorage.getItem('visitorId') || '';
    }
    return '';
};

const initialState: UserState = {
    isAuthenticated: false,
    visitorId: getInitialVisitorId(), // Load from localStorage if available
    name: '',
    email: '',
    ageGroup: '',
    nationality: '',
    selfieUrl: '',
    language: '', // Empty string to trigger language selection on first load
    hasCompletedRegistration: false,
    privacyAccepted: false,
    message: '',
    isLoading: false,
    apiError: null,
    createdAt: null,
    lastActivityAt: null,
    dataWasExpired: false,
};

// Async thunk for visitor signup
export const signupVisitor = createAsyncThunk<
    VisitorSignupResponse,
    VisitorSignupData,
    { rejectValue: string }
>(
    'user/signupVisitor',
    async (signupData, { rejectWithValue }) => {
        try {
            const response = await timeCapsuleApi.signupVisitor(signupData);
            
            // Track signup event
            if (response.visitorId) {
                try {
                    // await timeCapsuleApi.trackEvent({
                    //     visitorId: response.visitorId,
                    //     eventType: 'visitor_signup',
                    //     metadata: {
                    //         name: signupData.name,
                    //         nationality: signupData.nationality,
                    //         language: signupData.language,
                    //         ageGroup: signupData.ageGroup
                    //     }
                    // });
                } catch (eventError) {
                    console.warn('Failed to track signup event:', eventError);
                }
            }
            
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            return rejectWithValue(apiError.message || 'Failed to signup visitor');
        }
    }
);

// Async thunk for fetching visitor data
export const fetchVisitorData = createAsyncThunk<
    any, // We'll define a proper type when we know the API response structure
    string, // visitorId parameter
    { rejectValue: string }
>(
    'user/fetchVisitorData',
    async (visitorId, { rejectWithValue, dispatch }) => {
        try {
            console.log('📥 Fetching visitor data for:', visitorId);
            
            // Check for visitor ID cookie mismatch before making the API call
            const cookies = document.cookie.split(';').reduce((acc, cookie) => {
                const [key, value] = cookie.trim().split('=');
                acc[key] = value;
                return acc;
            }, {} as Record<string, string>);
            
            const cookieVisitorId = cookies['v'];
            
            if (cookieVisitorId && cookieVisitorId !== visitorId) {
                console.warn('🔄 Visitor ID sync needed:', {
                    reduxVisitorId: visitorId,
                    cookieVisitorId: cookieVisitorId,
                    action: 'Updating Redux state to match cookie'
                });
                
                // Update Redux state to match the cookie visitor ID
                // This ensures consistency between frontend state and backend authentication
                (dispatch as any)(setVisitorId(cookieVisitorId));
                
                // Use the cookie visitor ID for the API call
                const response = await timeCapsuleApi.getVisitor(cookieVisitorId);
                console.log('📥 Fetched visitor data (after sync):', response);
                return response;
            }
            
            const response = await timeCapsuleApi.getVisitor(visitorId);
            console.log('📥 Fetched visitor data:', response);
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            console.error('❌ Failed to fetch visitor data:', {
                error: apiError,
                visitorId: visitorId,
                status: apiError.status,
                message: apiError.message
            });
            return rejectWithValue(apiError.message || 'Failed to fetch visitor data');
        }
    }
);

// Async thunk for selfie upload
export const uploadSelfie = createAsyncThunk<
    SelfieUploadResponse,
    { visitorId: string; selfieDataUrl: string; filename?: string },
    { rejectValue: string }
>(
    'user/uploadSelfie',
    async ({ visitorId, selfieDataUrl, filename = 'selfie.jpg' }, { rejectWithValue }) => {
        try {
            // Convert data URL to Blob
            const selfieBlob = dataURLtoBlob(selfieDataUrl);
            
            const response = await timeCapsuleApi.uploadSelfie(visitorId, selfieBlob, filename);
            
            // Track selfie upload event
            try {
                // await timeCapsuleApi.trackEvent({
                //     visitorId,
                //     eventType: 'selfie_uploaded',
                //     metadata: {
                //         filename: response.filename || filename,
                //         size: selfieBlob.size
                //     }
                // });
            } catch (eventError) {
                console.warn('Failed to track selfie upload event:', eventError);
            }
            
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            return rejectWithValue(apiError.message || 'Failed to upload selfie');
        }
    }
);

// Async thunk for edited selfie upload
export const uploadEditedSelfie = createAsyncThunk<
    SelfieUploadResponse,
    { visitorId: string; selfieDataUrl: string; filename?: string },
    { rejectValue: string }
>(
    'user/uploadEditedSelfie',
    async ({ visitorId, selfieDataUrl, filename = 'edited-selfie.jpg' }, { rejectWithValue }) => {
        try {
            // Convert data URL to Blob
            const selfieBlob = dataURLtoBlob(selfieDataUrl);
            
            const response = await timeCapsuleApi.uploadEditedSelfie(visitorId, selfieBlob, filename);
            
            // Track edited selfie upload event
            try {
                // await timeCapsuleApi.trackEvent({
                //     visitorId,
                //     eventType: 'edited_selfie_uploaded',
                //     metadata: {
                //         filename: response.filename || filename,
                //         size: selfieBlob.size
                //     }
                // });
            } catch (eventError) {
                console.warn('Failed to track edited selfie upload event:', eventError);
            }
            
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            return rejectWithValue(apiError.message || 'Failed to upload edited selfie');
        }
    }
);

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserProfile: (state, action: PayloadAction<Partial<UserState>>) => {
            const now = Date.now();
            return { 
                ...state, 
                ...action.payload,
                // Set createdAt if this is the first time setting user data
                createdAt: state.createdAt || now,
                lastActivityAt: now,
                apiError: null // Clear any previous API errors
            };
        },
        setAuthenticated: (state, action: PayloadAction<boolean>) => {
            state.isAuthenticated = action.payload;
            state.lastActivityAt = Date.now();
        },
        setSelfie: (state, action: PayloadAction<string>) => {
            state.selfieUrl = action.payload;
            state.lastActivityAt = Date.now();
        },
        setLanguage: (state, action: PayloadAction<string>) => {
            const now = Date.now();
            state.language = action.payload;
            state.createdAt = state.createdAt || now;
            state.lastActivityAt = now;
            
            // Change i18n language when language is set in Redux
            i18n.changeLanguage(action.payload);
            
            // Explicitly set HTML lang attribute as fallback
            document.documentElement.setAttribute('lang', action.payload);
        },
        completeRegistration: (state) => {
            const now = Date.now();
            state.hasCompletedRegistration = true;
            state.isAuthenticated = true;
            state.createdAt = state.createdAt || now;
            state.lastActivityAt = now;
            // Set the auth cookie
            document.cookie = "authToken=authenticated; path=/; max-age=86400";
        },
        // Clear API error
        clearApiError: (state) => {
            state.apiError = null;
        },
        // Set visitor ID (useful for restoring from localStorage)
        setVisitorId: (state, action: PayloadAction<string>) => {
            state.visitorId = action.payload;
            if (action.payload) {
                localStorage.setItem('visitorId', action.payload);
            }
            state.lastActivityAt = Date.now();
        },
        // Update last activity timestamp
        updateActivity: (state) => {
            state.lastActivityAt = Date.now();
        },
        // Clear the dataWasExpired flag after handling
        clearDataExpiredFlag: (state) => {
            state.dataWasExpired = false;
        },
        // Check if user data has expired and reset if needed
        checkExpiration: (state) => {
            const now = Date.now();
            
            if (state.lastActivityAt && (now - state.lastActivityAt) > USER_DATA_EXPIRATION_MS) {
                // Data has expired, reset to initial state but preserve visitorId and language
                const preservedVisitorId = state.visitorId;
                const preservedLanguage = state.language;
                
                // Reset to initial state
                Object.assign(state, initialState);
                
                // Restore preserved data
                if (preservedVisitorId) {
                    state.visitorId = preservedVisitorId;
                }
                if (preservedLanguage) {
                    state.language = preservedLanguage;
                }
                
                // Set a flag to indicate data was expired (will be used for navigation)
                state.dataWasExpired = true;
                
                return;
            }
            
            // Update activity if not expired
            state.lastActivityAt = now;
        },
        logout: (state) => {
            // Clear all user data except language preference
            const language = state.language;
            Object.assign(state, { ...initialState, language });
            // Remove the auth cookie
            document.cookie = "authToken=; path=/; max-age=0";
            // Clear selfie and visitorId from localStorage
            localStorage.removeItem('selfie');
            localStorage.removeItem('visitorId');
        },
        // Action to completely reset user state (for testing or admin purposes)
        resetUserState: () => {
            // Clear localStorage data
            localStorage.removeItem('selfie');
            localStorage.removeItem('visitorId');
            return initialState;
        },
    },
    extraReducers: (builder) => {
        // Handle the PURGE action from redux-persist
        builder.addCase(PURGE, () => {
            return initialState;
        });
        
        // Handle visitor signup
        builder.addCase(signupVisitor.pending, (state) => {
            state.isLoading = true;
            state.apiError = null;
        });
        builder.addCase(signupVisitor.fulfilled, (state, action) => {
            state.isLoading = false;
            state.visitorId = action.payload.visitorId;
            state.message = action.payload.message || 'Signup successful';
            state.apiError = null;
            const now = Date.now();
            state.createdAt = state.createdAt || now;
            state.lastActivityAt = now;
            
            // Save visitorId to localStorage for persistence
            localStorage.setItem('visitorId', action.payload.visitorId);
            
            // Log successful signup for debugging
            console.log('✅ Visitor signup successful! ID:', action.payload.visitorId);
            console.log('💾 Visitor ID saved to localStorage');
        });
        builder.addCase(signupVisitor.rejected, (state, action) => {
            state.isLoading = false;
            state.apiError = action.payload || 'Failed to signup';
        });
        
        // Handle visitor data fetching
        builder.addCase(fetchVisitorData.pending, (state) => {
            state.isLoading = true;
            state.apiError = null;
        });
        builder.addCase(fetchVisitorData.fulfilled, (state, action) => {
            state.isLoading = false;
            state.apiError = null;
            
            // Restore user data from API response
            const visitorData = action.payload;
            console.log('🔍 Raw API response for visitor data:', visitorData);
            
            if (visitorData) {
                const beforeState = {
                    name: state.name,
                    email: state.email,
                    ageGroup: state.ageGroup,
                    nationality: state.nationality
                };
                
                state.name = visitorData.name || '';
                state.email = visitorData.email || '';
                
                // Enhanced ageGroup handling with detailed logging
                console.log('🔍 AgeGroup field analysis:', {
                    rawAgeGroup: visitorData.ageGroup,
                    ageGroupType: typeof visitorData.ageGroup,
                    ageGroupValue: visitorData.ageGroup,
                    isNull: visitorData.ageGroup === null,
                    isUndefined: visitorData.ageGroup === undefined,
                    isEmpty: visitorData.ageGroup === '',
                });
                
                // Handle ageGroup conversion - API should return ageGroup field
                let processedAgeGroup: AgeGroupValue | 'age_undisclosed' | '' = '';
                if (visitorData.ageGroup && typeof visitorData.ageGroup === 'string') {
                    processedAgeGroup = visitorData.ageGroup as AgeGroupValue;
                }
                state.ageGroup = processedAgeGroup;
                
                state.nationality = visitorData.nationality || '';
                state.hasCompletedRegistration = true;
                state.isAuthenticated = true;
                
                // Update timestamps
                const now = Date.now();
                state.lastActivityAt = now;
                state.createdAt = state.createdAt || now;
                
                const afterState = {
                    name: state.name,
                    email: state.email,
                    ageGroup: state.ageGroup,
                    nationality: state.nationality
                };
                
                console.log('✅ Visitor data restored to Redux store');
                console.log('📊 State before:', beforeState);
                console.log('📊 State after:', afterState);
                
                // Log if ageGroup is still empty after processing
                if (!state.ageGroup) {
                    console.warn('⚠️ AgeGroup field is still empty after processing API response');
                }
            } else {
                console.warn('⚠️ No visitor data received from API');
            }
        });
        builder.addCase(fetchVisitorData.rejected, (state, action) => {
            state.isLoading = false;
            state.apiError = action.payload || 'Failed to fetch visitor data';
        });
        
        // Handle selfie upload
        builder.addCase(uploadSelfie.pending, (state) => {
            state.isLoading = true;
            state.apiError = null;
        });
        builder.addCase(uploadSelfie.fulfilled, (state, action) => {
            state.isLoading = false;
            state.message = action.payload.message || 'Selfie uploaded successfully';
            state.apiError = null;
            state.lastActivityAt = Date.now();
        });
        builder.addCase(uploadSelfie.rejected, (state, action) => {
            state.isLoading = false;
            state.apiError = action.payload || 'Failed to upload selfie';
        });
        
        // Handle edited selfie upload
        builder.addCase(uploadEditedSelfie.pending, (state) => {
            state.isLoading = true;
            state.apiError = null;
        });
        builder.addCase(uploadEditedSelfie.fulfilled, (state, action) => {
            state.isLoading = false;
            state.message = action.payload.message || 'Edited selfie uploaded successfully';
            state.apiError = null;
            state.lastActivityAt = Date.now();
        });
        builder.addCase(uploadEditedSelfie.rejected, (state, action) => {
            state.isLoading = false;
            state.apiError = action.payload || 'Failed to upload edited selfie';
        });
    },
});

export const { 
    setUserProfile, 
    setAuthenticated, 
    setSelfie, 
    setLanguage,
    completeRegistration,
    clearApiError,
    setVisitorId,
    updateActivity,
    clearDataExpiredFlag,
    checkExpiration,
    logout,
    resetUserState
} = userSlice.actions;

export default userSlice.reducer; 